import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  getBoardingDroppingPoints,
  getRazorpayKeyData,
  getRidePostBoardingDroppingPoints,
  getCategoryTags,
  getLocalisations,
} from "../utils/api.functions";

// Create the context
const BoardingDroppingContext = createContext();

// Create a custom hook to use the BoardingDroppingContext
export const useBoardingDropping = () => {
  return useContext(BoardingDroppingContext);
};

// Create the provider component
export const BoardingDroppingProvider = ({ children }) => {
  const [boardingDroppingPoints, setBoardingDroppingPoints] = useState(null);
  const [ridePostBoardingDroppingPoints, setRidePostBoardingDroppingPoints] =
    useState(null);
  const [categoryTags, setCategoryTags] = useState([]);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [localisations, setLocalisations] = useState({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    // When popup opens, set overflow-x to hidden
    if (isPopupOpen) {
      document.body.style.overflowX = "hidden";
    } else {
      document.body.style.overflowX = ""; // Revert to the default
    }

    // Clean up on component unmount or when popup is closed
    return () => {
      document.body.style.overflowX = ""; // Ensure it resets on unmount
    };
  }, [isPopupOpen]);

  const togglePopup = useCallback(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  // Function to fetch boarding and dropping points from the API
  const fetchBoardingDroppingPoints = async () => {
    try {
      const apiKey = sessionStorage.getItem("key");
      const response = await getBoardingDroppingPoints({}, apiKey);
      if (response.status === 200) {
        if (response?.data?.boarding && response?.data?.dropping) {
          setBoardingDroppingPoints(response?.data);
        }
      }
    } catch (error) {
      console.log("Error fetching boarding and dropping points:", error);
    }
  };

  // Function to fetch boarding and dropping points from the API
  const fetchRidePostBoardingDroppingPoints = async () => {
    try {
      const apiKey = sessionStorage.getItem("key");
      const response = await getRidePostBoardingDroppingPoints({}, apiKey);
      if (response.status === 200) {
        if (response?.data?.boarding && response?.data?.dropping) {
          setRidePostBoardingDroppingPoints(response?.data);
        }
      }
    } catch (error) {
      console.log("Error fetching boarding and dropping points:", error);
    }
  };

  // Function to fetch Razorpay Key from the API
  const fetchRazorpayKeys = async () => {
    try {
      const apiKey = sessionStorage.getItem("key");
      const response = await getRazorpayKeyData({}, apiKey);
      if (response.status === 200) {
        if (response?.data?.resDecrypt?.response) {
          setRazorpayKey(response?.data?.resDecrypt?.response);
        }
      }
    } catch (error) {
      console.log("Error fetching:", error);
    }
  };

  const fetchCategoryTagsList = async () => {
    let apikey = sessionStorage.getItem("key");

    try {
      const response = await getCategoryTags({}, apikey);
      if (response.status === 200) {
        const newData = response?.data?.resDecrypt?.response;

        if (newData && newData.length > 0) {
          setCategoryTags(newData);
        }
      }
    } catch (error) {
      console.log(`Category Tags Error`, error);
    }
  };

  const fetchLocalisations = async () => {
    try {
      const apiKey = sessionStorage.getItem("key");
      const response = await getLocalisations({}, apiKey);
      if (response?.status === 200 && response?.data?.resDecrypt?.response != null) {
        const map = {};
        const data = response.data.resDecrypt.response;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (typeof data === "object") {
          items = Object.entries(data).map(([key, value]) => ({ key, value }));
        }
        items.forEach((item) => {
          if (item?.key != null) map[item.key] = item.value;
        });
        setLocalisations(map);
      }
    } catch (error) {
      console.log("Error fetching localisations:", error);
    }
  };

  // Fetch data when the component mounts
  useEffect(() => {
    fetchBoardingDroppingPoints();
    fetchRazorpayKeys();
    fetchRidePostBoardingDroppingPoints();
    fetchCategoryTagsList();
    fetchLocalisations();
  }, []);

  // Context value that will be shared
  const contextValue = useMemo(
    () => ({
      boardingDroppingPoints,
      ridePostBoardingDroppingPoints,
      fetchBoardingDroppingPoints,
      fetchRidePostBoardingDroppingPoints,
      razorpayKey,
      fetchRazorpayKeys,
      togglePopup,
      categoryTags,
      fetchCategoryTagsList,
      localisations,
      fetchLocalisations,
    }),
    [
      boardingDroppingPoints,
      ridePostBoardingDroppingPoints,
      razorpayKey,
      togglePopup,
      categoryTags,
      localisations,
    ]
  );

  return (
    <BoardingDroppingContext.Provider value={contextValue}>
      {children}
    </BoardingDroppingContext.Provider>
  );
};
